/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import AddressSuggestion from "@eisinc/App/Components/AddressSuggestionCard/AddressSuggestion";
import validateAddressWithAvalara from "@eisinc/App/Handlers/ValidateAddresswithAvalara";
import validateCounty from "@eisinc/App/Handlers/ValidateCounty";
import { xAddressValidationUpdate } from "@eisinc/App/Handlers/XNG/AddressValidationSuggestion";
import { AddressValidationModel } from "@eisinc/App/Services/AddressValidationWithAvalaraService";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { prepareAddressForValidation } from "@eisinc/App/Utilities/helpers";
import Button from "@eisinc/MobiusOverrides/Button";
import Modal from "@eisinc/MobiusOverrides/Modal";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { Radio, RadioGroup } from "@insite/mobius/index";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    addressSuggestionModalIsOpen?: boolean;
    validate?: any;
    title?: string;
    addressSuggestionsSkipButton?: string;
    hideSkipButton?: boolean;
    address?: ShipToModel;
}

const mapStateToProps = (state: ApplicationState) => ({
    countries: getCurrentCountries(state),
    session: getSession(state),
    addressValidationState: (state as CustomAppState).xAddressValidation.addressValidationState,
});

const mapDispatchToProps = {
    validateAddressWithAvalara,
    validateCounty,
    xAddressValidationUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const AvalaraValidationModal = forwardRef<OwnProps, Props>(
    (
        {
            validateAddressWithAvalara,
            title,
            countries,
            validateCounty,
            session,
            addressValidationState,
            xAddressValidationUpdate,
            address,
        },
        ref,
    ) => {
        const [addressSuggestionModalIsOpen, setAddressSuggestionModalIsOpen] = React.useState(false);
        const [addressSuggestionList, setAddressSuggestionList] = React.useState([] as AddressValidationModel[]);
        const [addressValidationErrorModalIsOpen, setAddressValidationErrorModalIsOpen] = React.useState(false);
        const [addressValidationError, setAddressValidationError] = React.useState("");
        const [selectedAddressSuggestionId, setSelectedAddressSuggestionId] = React.useState("");
        const [formAddressDraft, setFormAddressDraft] = React.useState({} as BillToModel);
        const [countyList, setCountyList] = useState([]);
        const [isCountyListModalOpen, setIsCountyListModalOpen] = useState(false);
        const [isCountyListModalOpenOnce, setIsCountyListModalOpenOnce] = useState(false);
        const [selectedCounty, setSelectedCounty] = useState("");
        const [area, setArea] = useState("");
        const [callback, setCallback] = useState(() => (param1: any) => {});
        const [onModalOpen, setOnModalOpen] = useState(() => () => {});
        const [currentTypedAddress, setCurrentTypedAddress] = useState<AddressValidationModel>(
            {} as AddressValidationModel,
        );

        const resolve = useRef((address?: any): any => {});

        useEffect(() => {
            const typedAddress = {
                addressLine1: address?.address1 || "",
                addressLine2: address?.address2 || "",
                addressLine3: address?.address3 || "",
                city: address?.city || "",
                state: address?.state?.name || "",
                postalCode: address?.postalCode || "",
                country: address?.country?.name || "",
            } as AddressValidationModel;
            setCurrentTypedAddress(typedAddress);
        }, [address]);

        useEffect(() => {
            const countyListLength = countyList?.length;

            if (selectedCounty && countyListLength) {
                if (countyListLength > 1) {
                    if (!isCountyListModalOpenOnce) {
                        setIsCountyListModalOpen(true);
                        setIsCountyListModalOpenOnce(true);

                        if (area === "createNewAccount") {
                            onModalOpen();
                        }

                        return;
                    }

                    if (!isCountyListModalOpen) {
                        callback(selectedCounty);

                        if (area === "checkoutShippingPageCheckBilling") {
                            setIsCountyListModalOpenOnce(false);
                        }
                    }
                }
            }
        }, [selectedCounty, countyList, isCountyListModalOpen]);

        useEffect(() => {
            const countyListLength = countyList?.length;

            if (selectedCounty && countyListLength) {
                if (countyListLength === 1) {
                    callback(selectedCounty);
                }
            }
        }, [selectedCounty]);

        const saveSelectedAddressSuggestion = (id: string | undefined) => {
            const selectedAddressSuggestion = addressSuggestionList.find(address => {
                return address.id === (id || selectedAddressSuggestionId);
            });

            if (selectedAddressSuggestion) {
                const currentCountry = countries?.find(
                    country => country.abbreviation === selectedAddressSuggestion.country,
                );
                const currentState = currentCountry?.states?.find(
                    state =>
                        state.abbreviation === selectedAddressSuggestion.state ||
                        state.abbreviation === selectedAddressSuggestion.region,
                );
                const address = {
                    address1: selectedAddressSuggestion.addressLine1 || selectedAddressSuggestion.line1,
                    address2: selectedAddressSuggestion.addressLine2 || selectedAddressSuggestion.line2,
                    address3: selectedAddressSuggestion.addressLine3 || selectedAddressSuggestion.line3,
                    city: selectedAddressSuggestion.city,
                    country: {
                        abbreviation: currentCountry?.abbreviation,
                        id: currentCountry?.id,
                        name: currentCountry?.name,
                        properties: {},
                        uri: currentCountry?.uri,
                    },
                    postalCode: selectedAddressSuggestion.postalCode,
                    state: {
                        abbreviation: currentState?.abbreviation,
                        id: currentState?.id,
                        name: currentState?.name,
                        properties: {},
                        uri: currentState?.uri,
                    },
                };
                addressSuggestionModalCloseHandler();
                resolve.current({ ...formAddressDraft, ...address });
            }
        };

        const addressSuggestionModalCloseHandler = () => {
            setAddressSuggestionModalIsOpen(false);
        };

        const addressValidationErrorModalCloseHandler = () => {
            setAddressValidationErrorModalIsOpen(false);
        };

        const forceAddressValidation = session.properties.forceAddressValidation;

        const addressSuggestionsSkip = () => {
            if (forceAddressValidation?.toLowerCase() === "true") {
                addressSuggestionModalCloseHandler();
                xAddressValidationUpdate({ addressValidationState: true });
            } else {
                addressSuggestionModalCloseHandler();
                resolve.current(formAddressDraft);
            }
        };

        const validate = (address: any, onModalOpen: any) => {
            setOnModalOpen(() => onModalOpen);
            setSelectedCounty("");

            return new Promise((res: any, reject: any) => {
                resolve.current = res;
                validateAddressWithAvalara({
                    address: prepareAddressForValidation(address),
                    onComplete: response => {
                        let localResults: {};

                        try {
                            localResults = JSON.parse(response?.apiResult?.errorJson?.message);
                        } catch (e) {
                            localResults = {};
                        }
                        if (response?.apiResult?.status === 400 && (forceAddressValidation?.toLowerCase() === "true" || (localResults as any).isPoBoxAddress)) {
                            const error = JSON.parse(response?.apiResult?.errorMessage).AvalaraErrorMessage;
                            setAddressValidationError(error);
                            setAddressValidationErrorModalIsOpen(true);
                            reject(error);
                        } else if ((localResults as any).skipAddressValidation) {
                            resolve.current(address);
                        } else if (response?.apiResult?.isSameOriginalAddress) {
                            resolve.current(address);
                        } else if (response?.apiResult?.validatedAddresses?.length) {
                            onModalOpen && onModalOpen();
                            setFormAddressDraft(address);
                            setAddressSuggestionList(
                                response.apiResult.validatedAddresses.map(
                                    (validatedAddress: AddressValidationModel, index: number) => {
                                        (validatedAddress as any).id = `${index}, ${
                                            validatedAddress.addressLine1 || validatedAddress.line1
                                        },  ${validatedAddress.city}, ${
                                            validatedAddress.state || validatedAddress.region
                                        }  ${validatedAddress.postalCode}, ${validatedAddress.country}`;
                                        return validatedAddress;
                                    },
                                ),
                            );
                            setAddressSuggestionModalIsOpen(true);
                        }
                    },
                });
            });
        };

        const countyValidation = (address: any, callback: any, area?: any) => {
            validateCounty({
                address: prepareAddressForValidation(address),
                onSuccess: (response: any) => {
                    const countyList = response?.countyList || [];
                    if (!countyList.length) {
                        callback(selectedCounty);
                        return;
                    }

                    setArea(area);
                    setCallback(() => callback);
                    setCountyList(countyList);
                    setSelectedCounty(countyList[0] || "");
                },
                onError: (error: any) => {
                    callback(selectedCounty);
                },
            });
        };

        useImperativeHandle(ref, () => ({ validate, countyValidation }));

        return (
            <>
                <AddressSuggestion
                    addressSuggestionModalIsOpen={addressSuggestionModalIsOpen}
                    addressSuggestionModalCloseHandler={addressSuggestionModalCloseHandler}
                    selectedAddressSuggestionId={selectedAddressSuggestionId}
                    saveSelectedAddressSuggestion={saveSelectedAddressSuggestion}
                    addressSuggestionList={addressSuggestionList}
                    setSelectedAddressSuggestionId={setSelectedAddressSuggestionId}
                    addressValidationError={addressValidationError}
                    addressValidationErrorModalIsOpen={addressValidationErrorModalIsOpen}
                    addressValidationErrorModalCloseHandler={addressValidationErrorModalCloseHandler}
                    addressSuggestionsSkip={addressSuggestionsSkip}
                    title={title}
                    addressSuggestionsSkipButton={translate("Close")}
                    forcedAvalara={forceAddressValidation?.toLowerCase() === "true"}
                    address={currentTypedAddress}
                />
                <Modal
                    headline={translate("Select County")}
                    sizeVariant="small"
                    isOpen={isCountyListModalOpen}
                    isCloseable={false}
                >
                    {countyList && countyList.length > 1 && (
                        <RadioGroup
                            value={selectedCounty}
                            onChangeHandler={e => {
                                setSelectedCounty(e.target.value);
                            }}
                        >
                            {countyList.map(county => {
                                return <Radio key={county}>{county}</Radio>;
                            })}
                        </RadioGroup>
                    )}
                    <StyledWrapper className="modal-actions">
                        <Button onClick={() => setIsCountyListModalOpen(false)}>{translate("Continue")}</Button>
                    </StyledWrapper>
                </Modal>
            </>
        );
    },
);

AvalaraValidationModal.displayName = "AvalaraValidation";

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(AvalaraValidationModal);
